import React, {useState, useEffect, useMemo} from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    IconButton,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import {toast} from 'react-toastify';
import {buildApi} from 'utils/api';
import {getBuildModeLabel} from 'utils/buildMode';

const statusColors = {
    pending: 'warning',
    running: 'info',
    success: 'success',
    failed: 'error',
    cancelled: 'default',
};

const statusLabels = {
    pending: '대기',
    running: '진행',
    success: '성공',
    failed: '실패',
    cancelled: '취소',
};

const rowsPerPageOptions = [10, 50, 100];

export default function BuildHistoryPage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [builds, setBuilds] = useState([]);
    const [pagination, setPagination] = useState({page: 1, limit: 10, total: 0, totalPages: 0});

    // Filter options (from server)
    const [filterOptions, setFilterOptions] = useState({
        products: [], requesters: [],
    });

    // Active filters
    const [filters, setFilters] = useState({
        productId: '',
        profile: '',
        mode: '',
        requestedBy: '',
    });

    // 선택된 제품에 맞는 프로파일 목록
    const profileOptions = useMemo(() => {
        if (!filters.productId) return [];
        const product = filterOptions.products.find(p => p.id === filters.productId);
        return product?.profiles || [];
    }, [filters.productId, filterOptions.products]);

    useEffect(() => {
        loadFilterOptions();
    }, []);

    useEffect(() => {
        loadBuilds(1, pagination.limit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const loadFilterOptions = async () => {
        try {
            const {data} = await buildApi.getFilterOptions();
            setFilterOptions(data);
        } catch (err) {
            console.error('Failed to load filter options:', err);
        }
    };

    const loadBuilds = async (page, limit) => {
        const p = page || pagination.page;
        const l = limit || pagination.limit;
        try {
            const {data} = await buildApi.getBuilds(l, p, filters);
            setBuilds(data.builds || []);
            if (data.pagination) {
                setPagination(data.pagination);
            }
        } catch (err) {
            console.error('Failed to load builds:', err);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => {
            const next = {...prev, [key]: value};
            // 제품 변경 시 프로파일 초기화
            if (key === 'productId') {
                next.profile = '';
            }
            return next;
        });
    };

    const handleClearFilters = () => {
        setFilters({productId: '', profile: '', mode: '', requestedBy: ''});
    };

    const hasActiveFilters = Object.values(filters).some(v => v !== '');

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    const handleDeleteBuild = async (build) => {
        const message = build.status === 'success'
            ? '성공한 빌드입니다. 결과물 파일도 함께 삭제됩니다. 삭제하시겠습니까?'
            : '삭제하시겠습니까?';

        if (!window.confirm(message)) return;

        try {
            await buildApi.deleteBuild(build.id);
            toast.success('빌드가 삭제되었습니다.');
            loadBuilds();
        } catch (err) {
            toast.error(err.response?.data?.error || '삭제에 실패했습니다.');
        }
    };

    const [logDialog, setLogDialog] = useState({open: false, build: null, logs: [], loading: false});

    const handleOpenLogs = async (build) => {
        setLogDialog({open: true, build, logs: [], loading: true});
        try {
            const {data} = await buildApi.getBuild(build.id);
            setLogDialog(prev => ({...prev, logs: data.build?.logs || [], loading: false}));
        } catch (err) {
            setLogDialog(prev => ({...prev, logs: [], loading: false}));
            toast.error('로그를 불러올 수 없습니다.');
        }
    };

    const handleCloseLogs = () => {
        setLogDialog({open: false, build: null, logs: [], loading: false});
    };

    const [downloadingId, setDownloadingId] = useState(null);

    const handleDownloadBuild = async (buildId) => {
        setDownloadingId(buildId);
        try {
            await buildApi.downloadBuild(buildId);
        } finally {
            setDownloadingId(null);
        }
    };

    const {page = 1, limit = 10, total = 0, totalPages = 0} = pagination;
    const startRow = total === 0 ? 0 : (page - 1) * limit + 1;
    const endRow = Math.min(page * limit, total);

    return (
        <Box>
            {/* Filter Bar */}
            <Paper sx={{p: 2, mb: 3}}>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(auto-fit, minmax(120px, 1fr))' },
                    gap: 1.5,
                    alignItems: 'center',
                }}>
                    <TextField
                        select
                        label="제품"
                        value={filters.productId}
                        onChange={(e) => handleFilterChange('productId', e.target.value)}
                        size="small"
                        fullWidth
                    >
                        <MenuItem value="">전체</MenuItem>
                        {filterOptions.products.map((p) => (
                            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        label="프로파일"
                        value={filters.profile}
                        onChange={(e) => handleFilterChange('profile', e.target.value)}
                        size="small"
                        fullWidth
                        disabled={!filters.productId || profileOptions.length === 0}
                    >
                        <MenuItem value="">전체</MenuItem>
                        {profileOptions.map((p) => (
                            <MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        label="모드"
                        value={filters.mode}
                        onChange={(e) => handleFilterChange('mode', e.target.value)}
                        size="small"
                        fullWidth
                    >
                        <MenuItem value="">전체</MenuItem>
                        <MenuItem value="dev">개발</MenuItem>
                        <MenuItem value="stage">스테이지</MenuItem>
                        <MenuItem value="product">정식</MenuItem>
                    </TextField>

                    <TextField
                        select
                        label="요청자"
                        value={filters.requestedBy}
                        onChange={(e) => handleFilterChange('requestedBy', e.target.value)}
                        size="small"
                        fullWidth
                    >
                        <MenuItem value="">전체</MenuItem>
                        {filterOptions.requesters.map((r) => (
                            <MenuItem key={r.username} value={r.username}>
                                {r.role === 'admin' ? `${r.username} (admin)` : r.username}
                            </MenuItem>
                        ))}
                    </TextField>

                    {hasActiveFilters && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleClearFilters}
                            startIcon={<ClearIcon/>}
                        >
                            초기화
                        </Button>
                    )}
                </Box>
            </Paper>

            {/* Build Table */}
            <Paper sx={{p: { xs: 2, md: 3 }}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                    <Typography variant="h2">빌드 히스토리</Typography>
                    <Typography sx={{fontSize: '0.875rem', color: 'text.secondary'}}>
                        총 {total}건
                    </Typography>
                </Box>

                {isMobile ? (
                    /* Mobile Card Layout */
                    <Box>
                        {builds.length === 0 ? (
                            <Typography align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                빌드 기록이 없습니다.
                            </Typography>
                        ) : builds.map((build) => (
                            <Box
                                key={build.id}
                                sx={{
                                    p: 2, mb: 1.5, bgcolor: 'background.default',
                                    borderRadius: 1, border: 1, borderColor: 'divider',
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Chip
                                        label={statusLabels[build.status] || build.status}
                                        color={statusColors[build.status] || 'default'}
                                        size="small"
                                    />
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {build.status === 'success' && (
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                title="다운로드"
                                                disabled={downloadingId === build.id}
                                                onClick={() => handleDownloadBuild(build.id)}
                                            >
                                                {downloadingId === build.id
                                                    ? <CircularProgress size={18}/>
                                                    : <DownloadIcon fontSize="small"/>}
                                            </IconButton>
                                        )}
                                        {build.status === 'failed' && (
                                            <IconButton
                                                size="small"
                                                color="warning"
                                                title="로그 보기"
                                                onClick={() => handleOpenLogs(build)}
                                            >
                                                <DescriptionIcon fontSize="small"/>
                                            </IconButton>
                                        )}
                                        <IconButton
                                            size="small"
                                            color="error"
                                            title="삭제"
                                            disabled={build.status === 'running' || build.status === 'pending'}
                                            onClick={() => handleDeleteBuild(build)}
                                        >
                                            <DeleteIcon fontSize="small"/>
                                        </IconButton>
                                    </Box>
                                </Box>
                                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                                    {build.productName || '-'}
                                </Typography>
                                <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                                    {build.version || build.tag || '-'}
                                    {build.profile ? ` · ${build.profile}` : ''}
                                    {` · ${getBuildModeLabel(build.mode)}`}
                                </Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
                                    {build.requestedBy} · {formatDate(build.finishedAt || build.startedAt)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    /* Desktop Table Layout */
                    <TableContainer sx={{maxHeight: 500}}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>제품</TableCell>
                                    <TableCell>릴리즈</TableCell>
                                    <TableCell>프로파일</TableCell>
                                    <TableCell>모드</TableCell>
                                    <TableCell>상태</TableCell>
                                    <TableCell>요청자</TableCell>
                                    <TableCell>시작 시간</TableCell>
                                    <TableCell>완료 시간</TableCell>
                                    <TableCell align="center">작업</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {builds.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center">
                                            빌드 기록이 없습니다.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    builds.map((build) => (
                                        <TableRow key={build.id} hover>
                                            <TableCell>{build.productName || '-'}</TableCell>
                                            <TableCell>{build.version || build.tag || '-'}</TableCell>
                                            <TableCell>{build.profile || '-'}</TableCell>
                                            <TableCell>{getBuildModeLabel(build.mode)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={statusLabels[build.status] || build.status}
                                                    color={statusColors[build.status] || 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{build.requestedBy}</TableCell>
                                            <TableCell>{formatDate(build.startedAt)}</TableCell>
                                            <TableCell>{formatDate(build.finishedAt)}</TableCell>
                                            <TableCell align="center" sx={{whiteSpace: 'nowrap'}}>
                                                {build.status === 'success' && (
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        title="다운로드"
                                                        disabled={downloadingId === build.id}
                                                        onClick={() => handleDownloadBuild(build.id)}
                                                    >
                                                        {downloadingId === build.id
                                                            ? <CircularProgress size={18}/>
                                                            : <DownloadIcon fontSize="small"/>}
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    size="small"
                                                    color="warning"
                                                    title="로그 보기"
                                                    onClick={() => handleOpenLogs(build)}
                                                >
                                                    <DescriptionIcon fontSize="small"/>
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    title="삭제"
                                                    disabled={build.status === 'running' || build.status === 'pending'}
                                                    onClick={() => handleDeleteBuild(build)}
                                                >
                                                    <DeleteIcon fontSize="small"/>
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Pagination */}
                {total > 0 && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: 1.5,
                        mt: 2, pt: 2, borderTop: 1, borderColor: 'divider',
                    }}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <Typography sx={{fontSize: '0.8125rem', color: 'text.secondary'}}>
                                페이지당
                            </Typography>
                            <TextField
                                select
                                value={limit}
                                onChange={(e) => loadBuilds(1, parseInt(e.target.value))}
                                size="small"
                                sx={{width: 80}}
                            >
                                {rowsPerPageOptions.map((opt) => (
                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                ))}
                            </TextField>
                        </Box>

                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', sm: 'flex-end' }, gap: 0.5}}>
                            <Typography sx={{fontSize: '0.8125rem', color: 'text.secondary', mr: 0.5}}>
                                {startRow}-{endRow} / {total}건
                            </Typography>
                            <IconButton size="small" onClick={() => loadBuilds(1)} disabled={page <= 1}>
                                <FirstPageIcon fontSize="small"/>
                            </IconButton>
                            <IconButton size="small" onClick={() => loadBuilds(page - 1)} disabled={page <= 1}>
                                <NavigateBeforeIcon fontSize="small"/>
                            </IconButton>
                            <Typography sx={{fontSize: '0.8125rem', mx: 0.5}}>
                                {page} / {totalPages}
                            </Typography>
                            <IconButton size="small" onClick={() => loadBuilds(page + 1)} disabled={page >= totalPages}>
                                <NavigateNextIcon fontSize="small"/>
                            </IconButton>
                            <IconButton size="small" onClick={() => loadBuilds(totalPages)} disabled={page >= totalPages}>
                                <LastPageIcon fontSize="small"/>
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* 로그 다이얼로그 */}
            <Dialog
                open={logDialog.open}
                onClose={handleCloseLogs}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    빌드 로그 — {logDialog.build?.productName} {logDialog.build?.version}
                </DialogTitle>
                <DialogContent dividers>
                    {logDialog.loading ? (
                        <Box sx={{display: 'flex', justifyContent: 'center', p: 3}}>
                            <CircularProgress/>
                        </Box>
                    ) : !!logDialog.build?.error || logDialog.logs.length > 0 ? (
                        <Box>
                            {logDialog.build?.error && (
                                <>
                                <Typography variant="subtitle2" color="error" sx={{mb: 1}}>
                                    에러
                                </Typography>
                                <Box sx={{
                                    p: 1.5, mb: 2, bgcolor: 'error.50', border: 1, borderColor: 'error.200',
                                    borderRadius: 1, fontFamily: 'monospace', fontSize: '0.8125rem',
                                    whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                                }}>
                                    {logDialog.build.error}
                                </Box>
                            </>
                            )}
                            {logDialog.logs.length > 0 && (
                                <>
                                    <Typography variant="subtitle2" sx={{mb: 1}}>
                                        빌드 로그 ({logDialog.logs.length}건)
                                    </Typography>
                                    <Box sx={{
                                        maxHeight: 400, overflow: 'auto', bgcolor: '#1e1e1e',
                                        color: '#d4d4d4', p: 1.5, borderRadius: 1,
                                        fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.6,
                                    }}>
                                        {logDialog.logs.map((log, i) => (
                                            <Box key={i} sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-all'}}>
                                                {log.message}
                                            </Box>
                                        ))}
                                    </Box>
                                </>
                            )}
                        </Box>
                    ) : (
                        <Typography color="text.secondary">로그가 없습니다.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseLogs}>닫기</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
